import React, { useCallback, useRef } from 'react';
import { Pressable, StyleSheet, type View } from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../hooks';
import { BodyText } from '../../../Text';
import { type TabItemId } from '../../Tabs.types';

// ─────────────────────────────────────────────────────────────────────────────

export const TabButton = <Id extends TabItemId = TabItemId>(props: TabButtonProps<Id>) => {
  const { id, panelId, label, ariaLabel, isActive, isFluid, isDisabled, onChange } = props;

  const buttonRef = useRef<View>(null);
  const isHovered = useHovered(buttonRef);

  // ─── Animated Values ─────────────────────────────────────────────────

  const activeSV = useDerivedValue(() => {
    return withTransition(isActive ? 1 : 0);
  });

  const hoverSV = useDerivedValue(() => {
    return withTransition(isHovered && !isDisabled ? 1 : 0);
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const pressableAnimatedStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        activeSV.value,
        [0, 1],
        [theme.colors.OPACITY.DARK_KALE.TRANSPARENT, theme.colors.DARK_KALE],
      ),
    };
  });

  const labelAnimatedStyle = useAnimatedStyle(() => {
    return {
      color: interpolateColor(activeSV.value, [0, 1], [theme.colors.DARK_KALE, theme.colors.WHITE]),
    };
  });

  const hoverContainerAnimatedStyles = useAnimatedStyle(() => {
    return {
      opacity: hoverSV.value,
    };
  });

  const hoverContainerStyles = [styles.hoverContainer, hoverContainerAnimatedStyles];

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  const onPress = useCallback(() => {
    if (isActive) return;

    onChange(id);
  }, [id, isActive, onChange]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <AnimatedPressable
      key={id}
      ref={buttonRef}
      nativeID={id}
      style={[
        styles.container,
        isFluid ? styles.containerFluid : null,
        pressableAnimatedStyle,
        isDisabled ? styles.containerDisabled : null,
      ]}
      accessibilityRole="tab"
      aria-label={ariaLabel}
      accessibilityState={{ selected: isActive }}
      {...{ accessibilitySelected: isActive, accessibilityControls: panelId }}
      disabled={isDisabled}
      onPress={onPress}
    >
      <Animated.View style={hoverContainerStyles} />

      <AnimatedBodyText style={[styles.label, labelAnimatedStyle]} sizeMatch={['14']}>
        {label}
      </AnimatedBodyText>
    </AnimatedPressable>
  );
};

// ─── Animated Components ─────────────────────────────────────────────────────

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);
const AnimatedBodyText = Animated.createAnimatedComponent(BodyText);

// ─── Utils ───────────────────────────────────────────────────────────────────

function withTransition(value: number) {
  'worklet';

  return withTiming(value, { duration: theme.transitions.base });
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['6'],
    borderRadius: theme.radius.xlarge,
    userSelect: 'none',
  },
  containerFluid: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['2'],
  },
  containerDisabled: {
    opacity: 0.5,
  },
  hoverContainer: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
    borderRadius: theme.radius.xlarge,
  },
  label: {
    textAlign: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type TabButtonProps<Id extends TabItemId = TabItemId> = {
  id: Id;
  panelId: string;
  label: string;
  isActive: boolean;
  ariaLabel?: string;
  isDisabled?: boolean;
  isFluid?: boolean;
  onChange: (value: Id) => void;
};
